import type { ElementType } from "react"
import type { View } from "@/App"
import type { CurrentUser } from "@/lib/api"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ChevronDown,
  FileSpreadsheet,
  LayoutDashboard,
  LogOut,
  Server,
  Settings2,
  Shield,
  ShieldCheck,
  UserSquare2,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { ThemeModeSwitcher } from "@/components/ThemeModeSwitcher"
import { BrandMark } from "@/components/BrandMark"

type Props = {
  currentUser: CurrentUser
  machines: string[]
  selectedMachine: string
  activeView: View
  onMachineChange: (m: string) => void
  onViewChange: (v: View) => void
  onLogout: () => void
}

const navItems: {
  id: View
  label: string
  icon: ElementType
  permission?: string
}[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    permission: "view",
  },
  {
    id: "vpn-clients",
    label: "Usuários VPN",
    icon: Shield,
    permission: "view",
  },
  {
    id: "exports",
    label: "Exportações",
    icon: FileSpreadsheet,
    permission: "view",
  },
  {
    id: "admin",
    label: "Admin",
    icon: ShieldCheck,
    permission: "admin",
  },
]

export function AppSidebar({
  currentUser,
  machines,
  selectedMachine,
  activeView,
  onMachineChange,
  onViewChange,
  onLogout,
}: Props) {
  const { isMobile } = useSidebar()
  const initials = (currentUser.fullName || currentUser.username)
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()

  const visible = navItems.filter(
    (item) =>
      !item.permission ||
      currentUser.permissions.includes(item.permission as never)
  )

  return (
    <Sidebar variant="sidebar" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2.5 px-1 py-1.5">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/15">
                <BrandMark className="size-5 text-primary" />
              </div>
              <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                <span className="truncate text-xs font-semibold">VPN ONZ</span>
                <span className="truncate font-mono text-[10px] text-muted-foreground">
                  Painel de controle
                </span>
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {machines.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton
                        className="data-[state=open]:bg-sidebar-accent"
                        tooltip="Ambiente"
                      >
                        <Server className="shrink-0" />
                        <span className="truncate text-xs">
                          {selectedMachine || "Selecione"}
                        </span>
                        <ChevronDown className="ml-auto shrink-0 opacity-50" />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      side={isMobile ? "bottom" : "right"}
                      align="start"
                      className="w-56"
                    >
                      {machines.map((m) => (
                        <DropdownMenuItem
                          key={m}
                          onClick={() => onMachineChange(m)}
                          className="flex items-center justify-between"
                        >
                          <span className="truncate text-sm">{m}</span>
                          {m === selectedMachine && (
                            <Badge
                              variant="secondary"
                              className="ml-2 font-mono text-[10px]"
                            >
                              ativo
                            </Badge>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup className="flex-1">
          <SidebarGroupContent>
            <SidebarMenu>
              {visible.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={
                      activeView === item.id ||
                      (item.id === "vpn-clients" &&
                        activeView === "vpn-client-detail")
                    }
                    onClick={() => onViewChange(item.id)}
                    tooltip={item.label}
                  >
                    <item.icon className="shrink-0" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeView === "account"}
                  onClick={() => onViewChange("account")}
                  tooltip="Minha conta"
                >
                  <Settings2 className="shrink-0" />
                  <span>Minha conta</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <ThemeModeSwitcher variant="sidebar" />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                >
                  <Avatar className="size-7 shrink-0">
                    <AvatarFallback className="bg-primary/20 font-mono text-xs text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-xs font-medium">
                      {currentUser.fullName || currentUser.username}
                    </span>
                    <span className="truncate font-mono text-[10px] text-muted-foreground">
                      {currentUser.username}
                    </span>
                  </div>
                  <ChevronDown className="ml-auto shrink-0 opacity-50 group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isMobile ? "bottom" : "right"}
                align="end"
                className="w-48"
              >
                <DropdownMenuItem onClick={() => onViewChange("account")}>
                  <UserSquare2 className="mr-2 size-4" />
                  Minha conta
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 size-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
